import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3,
  Filter,
  TrendingUp,
  Users,
  CalendarClock,
  ListTodo,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import {
  fetchAnalyticsFilterOptions,
  type FilterOptionsData,
} from "./analyticsApi";
import OverviewTab from "./OverviewTab";
import FunnelTab from "./FunnelTab";
import OperatorsTab from "./OperatorsTab";
import FollowupsTab from "./FollowupsTab";
import TasksTab from "./TasksTab";
import QualityTab from "./QualityTab";
import InsightsTab from "./InsightsTab";

const TAB_IDS = ["overview", "funnel", "operators", "followups", "tasks", "quality", "insights"] as const;
type TabId = (typeof TAB_IDS)[number];

const DEFAULT_FILTERS: AnalyticsFilters = (() => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().split("T")[0],
    to: to.toISOString().split("T")[0],
  };
})();

const AnalyticsMain: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptionsData | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const loadFilterOptions = useCallback(async () => {
    try {
      setLoadingOptions(true);
      const data = await fetchAnalyticsFilterOptions();
      setFilterOptions(data);
    } catch {
      setFilterOptions({ tasks: [], operators: [] });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const setPreset = (days: number) => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    setFilters((prev) => ({
      ...prev,
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    }));
  };

  const tabs = [
    { id: "overview" as TabId, label: t("analytics.overview"), icon: BarChart3 },
    { id: "funnel" as TabId, label: t("analytics.funnel"), icon: Filter },
    { id: "operators" as TabId, label: t("analytics.operators"), icon: Users },
    { id: "followups" as TabId, label: t("analytics.followups"), icon: CalendarClock },
    { id: "tasks" as TabId, label: t("analytics.tasks"), icon: ListTodo },
    { id: "quality" as TabId, label: t("analytics.quality"), icon: ShieldCheck },
    { id: "insights" as TabId, label: t("analytics.insights"), icon: TrendingUp },
  ];

  const setToday = () => {
    const today = new Date().toISOString().split("T")[0];
    setFilters((prev) => ({ ...prev, from: today, to: today }));
  };
  const setYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.toISOString().split("T")[0];
    setFilters((prev) => ({ ...prev, from: y, to: y }));
  };
  const customButtons = [
    { id: "preset-today", label: t("analytics.today"), onClick: setToday },
    { id: "preset-yesterday", label: t("analytics.yesterday"), onClick: setYesterday },
    { id: "preset-7", label: t("analytics.last7"), onClick: () => setPreset(7) },
    { id: "preset-30", label: t("analytics.last30"), onClick: () => setPreset(30) },
    { id: "preset-90", label: t("analytics.last90"), onClick: () => setPreset(90) },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        icon={BarChart3}
        title={t("analytics.title")}
        description={t("analytics.description")}
        tabs={tabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
        }))}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        customButtons={customButtons}
      />

      <div className="w-full lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Filters bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3">
          <span className="text-sm font-medium text-gray-700 flex items-center">
            <RefreshCw className="w-4 h-4 mr-1 text-[#264a3e]" />
            {t("analytics.dateRange")}:
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-gray-600">{t("analytics.from")}</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#264a3e] focus:border-[#264a3e] text-sm"
            />
            <label className="text-sm text-gray-600">{t("analytics.to")}</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#264a3e] focus:border-[#264a3e] text-sm"
            />
          </div>
          {!loadingOptions && filterOptions && (
            <>
              <select
                value={filters.task ?? ""}
                onChange={(e) => setFilters((prev) => ({ ...prev, task: e.target.value || undefined }))}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#264a3e] focus:border-[#264a3e] text-sm"
              >
                <option value="">{t("analytics.task")} (all)</option>
                {filterOptions.tasks.map((task) => (
                  <option key={task} value={task}>
                    {task}
                  </option>
                ))}
              </select>
              <select
                value={filters.operatorId ?? ""}
                onChange={(e) => setFilters((prev) => ({ ...prev, operatorId: e.target.value || undefined }))}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#264a3e] focus:border-[#264a3e] text-sm"
              >
                <option value="">{t("analytics.operator")} (all)</option>
                {filterOptions.operators.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.username}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Tab content */}
        {activeTab === "overview" && (
          <OverviewTab filters={filters} onNavigateToTab={(tab) => setActiveTab(tab as TabId)} />
        )}
        {activeTab === "funnel" && <FunnelTab filters={filters} />}
        {activeTab === "operators" && <OperatorsTab filters={filters} />}
        {activeTab === "followups" && <FollowupsTab filters={filters} />}
        {activeTab === "tasks" && <TasksTab filters={filters} />}
        {activeTab === "quality" && <QualityTab />}
        {activeTab === "insights" && <InsightsTab filters={filters} />}
      </div>
    </div>
  );
};

export default AnalyticsMain;
